// The Funding Engine's home (FEHome.dc.html), worked out from the product's own rows
// (src/product/data.js) and the app's clock. Pure: no network and no React, so a test can import it.
//
// The board draws the home of a member whose intake is running, on the day of a live session: the
// next session (hero), the week to catch up on first, the 6 weeks, what's still to tick, how to reach
// Adam and Resources. From stage 10 nobody's intake is running (Cohort 3 ends 14 Oct), so the main
// state is a member with no running intake (Adam, 3 Oct): there's no session hero, and the week they
// should watch next leads the page instead. Sessions come back only for the member's own intake
// (row level security), so "no upcoming session" is the same test for a buyer with no intake and for
// an intake that has finished.
//
// The Funding Engine releases by intake (Adam, 10 Oct): each week's recording opens to an intake once
// its session for that week has started, and its worksheets and prompts once that session has run.
// A finished intake (Cohort 2 or 3, copied in with its sessions as history) has every week open and
// its own dates on them; someone with no intake gets every week any intake has had. Whether a week
// is open is the database's answer (its recording's content comes back), never worked out here.
//
// - hero: their next live session, when there is one: "Week 4 is live tonight", Join from
//   join_opens_minutes before (the Library's rule) and Add to calendar.
// - lead: where they are. The first week that's open to them and not watched: carry on from where
//   they stopped, or start it. Once every open week is watched: when the next week opens, the
//   homework still to tick before the certificate, or the certificate itself once it's issued.
//   With a hero it shows beside it only as "Catch up first" (or the certificate); without one it's
//   the page's lead, and its title is the page's heading.
// - weeks: every numbered week, with its intake's date where they have an intake. A week that's
//   open says how far they are; a week that isn't says when it's taught (FEHome draws its title as
//   "Taught live on Wed 7 Oct" until then). The week being taught today stays live until its
//   session ends, even once its recording has opened (beingTaught).
// - todo: the homework still to tick from the latest week they've watched (FEHome's "Still to tick
//   from Week 2"). Only kind 'homework' counts; Week 6's commitments never do.
// - help: "Stuck on anything?": WhatsApp while their intake runs (product_extras comes back only
//   then), the product's questions line otherwise (Adam, 3 Oct).
import * as f from '../../lib/format.js'
import { chapterAt } from '../../lib/timestamp.js'
import {
  homeworkFor, isReleased, moduleName, moduleRecordings, myIntake, nameWithSchedule, numberedModules, progressFor,
  sessionForModule, upcomingSessions,
} from '../../product/model.js'
import { readyLine } from '../../product/certificate.js'

const MIN = 60e3
const zoomLink = url => (typeof url === 'string' && /^https:\/\//.test(url) ? url : null)
const httpsLink = url => (typeof url === 'string' && /^https:\/\//.test(url) ? url : null)
const plural = (n, one, many) => `${n} ${n === 1 ? one : many}`
// A week's own title, or '' while it has none: a placeholder ("[Week 3 title]", as Add a product makes a new live
// cohort's weeks) or its label again, so the row says "Week 3" once, by its label (the product-types follow-ups, 13 Oct)
const ownTitle = m => (m.title && !f.isPlaceholder(m.title) && m.title !== m.label ? m.title : '')
const AFTER = 'The recording, notes and homework go up on this page after the session.'

// A session's moment, as the Library reads it (library/model.js): live, Join open, days away, evening
export function sessionMoment(session, product, nowMs) {
  const start = Date.parse(session.starts_at)
  const end = start + (session.duration_minutes || 0) * MIN
  const joinAt = start - (product.join_opens_minutes ?? 15) * MIN
  return {
    start, end, joinAt,
    live: start <= nowMs && nowMs < end,
    ended: end <= nowMs,
    joinOpen: nowMs >= joinAt && nowMs < end,
    days: f.daysFrom(nowMs, start),
    evening: f.uk(start).hh >= 17,
  }
}

// The name a session goes by on the home: its week's label ("Week 4"), else its own title
function sessionLabel(data, session) {
  const mod = session.module_id ? (data.modules || []).find(m => m.id === session.module_id) : null
  return mod?.label || session.title
}

// The next live session, as the hero draws it, or null
export function heroFor(data, product, nowMs) {
  const next = upcomingSessions(data, nowMs)[0]
  if (!next) return null
  const m = sessionMoment(next, product, nowMs)
  const label = sessionLabel(data, next)
  const at = f.time(m.start)
  let title
  if (m.live) title = `${label} is live now`
  else if (m.days === 0) title = `${label} is live ${m.evening ? 'tonight' : 'today'}`
  else if (m.days === 1) title = `${label} is live tomorrow`
  else if (m.days < 7) title = `${label} is live on ${f.weekday(m.start)}`
  else title = `${label} is live on ${f.dayMonthLong(m.start)}`

  let lede
  if (m.live) lede = `On Zoom until ${f.time(m.end)}. ${AFTER}`
  else if (m.days === 0) lede = `${at} on Zoom. ${m.joinOpen ? 'Join is open now.' : `The button opens at ${f.time(m.joinAt)}.`} ${AFTER}`
  else lede = `${f.longDate(m.start)}, ${at} on Zoom. The button opens here at ${f.time(m.joinAt)} on the day. ${AFTER}`

  let join = null
  if (m.live || m.joinOpen) join = zoomLink(next.zoom_join_url) ? { open: true, href: zoomLink(next.zoom_join_url), label: 'Join on Zoom' } : null
  else if (m.days === 0) join = { open: false, label: `Join opens at ${f.time(m.joinAt)}` }

  const intake = myIntake(data)
  return {
    sessionId: next.id,
    eyebrow: intake ? nameWithSchedule(intake.name, intake.schedule_label) : null,
    title,
    lede,
    live: m.live,
    join,
    calendar: m.live ? null : {
      uid: next.id, title: `${product.name}, ${next.title}`, startMs: m.start, endMs: m.end, path: product.path || '/',
    },
  }
}

// The week their intake is being taught today: its session is today and hasn't ended, and it's
// their first time through the week (no session of theirs for it has ended, so a resit's weeks stay
// open). The database opens a week's recording to an intake once its session has started, and The
// Funding Engine's recordings are shared and already switched on, so the week's recording can be
// open while its session runs. Until the session ends the home draws the week as FEHome.dc.html
// draws Week 4 on its day, live, and never offers it to catch up on: its worksheets and prompts open
// only after the session, and the hero says the recording goes up then.
export function beingTaught(data, product, moduleId, nowMs) {
  const mine = (data.sessions || []).filter(s => s.module_id === moduleId && s.status !== 'cancelled' && s.starts_at)
  const moments = mine.map(s => ({ s, m: sessionMoment(s, product, nowMs) }))
  if (moments.some(x => x.m.ended)) return null
  return moments.find(x => x.m.days === 0)?.s || null
}

// The weeks in order, each with its recording and whether it's open to them. With the product and
// the clock, a week being taught today counts as not open yet (beingTaught).
function weekList(data, product = null, nowMs = null) {
  return numberedModules(data).map(m => {
    const recs = moduleRecordings(data, m.id)
    const open = recs.filter(r => isReleased(data, r.id))
    const taught = product && nowMs != null ? Boolean(beingTaught(data, product, m.id, nowMs)) : false
    return { module: m, recs, open: taught ? [] : open, released: open.length > 0 && !taught }
  })
}

const watchedRec = (data, r) => Boolean(progressFor(data, r.id)?.watched_at)
const counted = (data, moduleId) => homeworkFor(data, moduleId).filter(h => h.kind !== 'commitment')

// Every open recording of a week watched
function weekWatched(data, w) {
  return w.released && w.open.every(r => watchedRec(data, r))
}

// When a week that isn't open yet opens, in the words of its row: "Opens after the session",
// "Recording to come", "Opens after the live session". Once its session has ended the recording is
// simply still to come: "goes up after the session" reads oddly weeks or months later (member review,
// 11 Oct).
function waitingLine(data, product, module, nowMs) {
  const s = sessionForModule(data, module.id)
  if (!s) return 'Opens after the live session'
  const m = sessionMoment(s, product, nowMs)
  if (m.ended) return 'Recording to come'
  if (m.live) return 'Join is open'
  if (m.days === 0) return m.joinOpen ? 'Join is open' : `Join opens at ${f.time(m.joinAt)}`
  return 'Opens after the session'
}

// Where they are: { kind, kicker, title, bar, line, button } or null; carry and start also name the
// recording (recordingId), for its thumbnail on the card (decision 47, 12 Oct)
export function leadFor(data, product, nowMs, { withHero = false } = {}) {
  const weeks = weekList(data, product, nowMs)
  const path = product.path || ''
  for (const w of weeks) {
    const r = w.open.find(x => !watchedRec(data, x))
    if (!r) continue
    const pr = progressFor(data, r.id)
    const c = (data.content || []).find(x => x.recording_id === r.id)
    const duration = c?.duration_seconds || 0
    const href = `${path}/${w.module.key}`
    const pos = pr?.position_seconds || 0
    if (pos > 10) {
      const chapter = chapterAt(c?.chapters || [], pos)
      return {
        kind: 'carry',
        recordingId: r.id,
        kicker: withHero ? 'Catch up first' : 'Carry on watching',
        title: moduleName(w.module),
        bar: duration ? Math.min(100, Math.round((pos / duration) * 100)) : null,
        line: `You stopped at ${f.clock(pos)}${chapter ? `, ${chapter.title}` : ''}`,
        button: { label: `Carry on from ${f.clock(pos)}`, href, play: true },
      }
    }
    return {
      kind: 'start',
      recordingId: r.id,
      kicker: withHero ? 'Catch up first' : 'Watch next',
      title: moduleName(w.module),
      bar: null,
      line: `${duration ? `${f.duration(duration)}. ` : ''}It picks up where you leave off, on any device.`,
      button: { label: `Watch ${w.module.label}`, href, play: true },
    }
  }

  // every open week watched
  if (data.certificate) {
    return {
      kind: 'finished',
      kicker: 'Your certificate',
      title: `You've finished ${product.name}`,
      bar: null,
      line: readyLine(product.certificate_rule, product.type),
      button: { label: 'See your certificate', href: `${path}/certificate` },
    }
  }
  const waiting = weeks.find(w => !w.released)
  if (waiting) {
    return {
      kind: 'opens',
      kicker: 'Watch next',
      title: waiting.module.label,
      bar: null,
      line: `${waitingLine(data, product, waiting.module, nowMs)}.`,
      button: null,
    }
  }
  const unticked = weeks.filter(w => counted(data, w.module.id).some(h => !(data.ticks || []).includes(h.id)))
  if (product.certificate_on && product.certificate_rule === 'all_watched_and_homework' && unticked.length) {
    const n = unticked.reduce((sum, w) => sum + counted(data, w.module.id).filter(h => !(data.ticks || []).includes(h.id)).length, 0)
    return {
      kind: 'homework',
      kicker: 'Nearly there',
      title: 'Tick off your homework',
      bar: null,
      line: n === 1
        ? '1 piece of homework still to tick. Your certificate is ready once it\'s done.'
        : `${n} pieces of homework still to tick. Your certificate is ready once they're done.`,
      button: { label: `Open ${unticked[0].module.label}`, href: `${path}/${unticked[0].module.key}` },
    }
  }
  if (!weeks.length) return null
  return {
    kind: 'done',
    kicker: `Your ${weeks.length} weeks`,
    title: 'Every week watched',
    bar: null,
    line: 'Every recording stays here. Go back through any of them whenever you like.',
    button: null,
  }
}

// "Your 6 weeks": { heading, dates, rows }
export function weekRows(data, product, nowMs) {
  const path = product.path || ''
  const weeks = weekList(data, product, nowMs)
  const rows = weeks.map(w => {
    const m = w.module
    const s = sessionForModule(data, m.id)
    const row = {
      id: m.id, key: m.key, label: m.label, date: s ? f.shortDate(s.starts_at) : null,
      title: ownTitle(m), status: '', tone: 'plain', done: false, href: null, action: null,
    }
    if (w.released) {
      row.href = `${path}/${m.key}`
      const r = w.open.find(x => !watchedRec(data, x)) || w.open[0]
      const pr = progressFor(data, r.id)
      if (weekWatched(data, w)) {
        const hw = counted(data, m.id)
        const ticked = hw.filter(h => (data.ticks || []).includes(h.id)).length
        row.status = hw.length ? `Watched. Homework ${ticked} of ${hw.length}` : 'Watched'
        row.tone = 'done'
        row.done = true
      } else if ((pr?.position_seconds || 0) > 10) {
        row.status = `Carry on from ${f.clock(pr.position_seconds)}`
        row.tone = 'carry'
      } else {
        row.status = 'Not started'
        row.tone = 'soft'
      }
      return row
    }
    if (!s) {
      row.status = 'Opens after the live session'
      row.tone = 'later'
      return row
    }
    const mo = sessionMoment(s, product, nowMs)
    if (!mo.ended && mo.days === 0) {
      row.title = mo.live ? 'Live now' : `Live ${mo.evening ? 'tonight' : 'today'} at ${f.time(mo.start)}`
      row.status = waitingLine(data, product, m, nowMs)
      row.tone = 'live'
      row.action = mo.evening ? 'Tonight' : 'Today'
      return row
    }
    row.title = `Taught live on ${f.shortDate(s.starts_at)}`
    row.status = waitingLine(data, product, m, nowMs)
    row.tone = 'later'
    return row
  })
  return { heading: `Your ${plural(rows.length, 'week', 'weeks')}`, dates: rows.some(r => r.date), rows }
}

// "Still to tick from Week 2": the latest week they've watched with homework left, or null.
// keep: the item ids shown when the page opened, so a tick doesn't make the list jump.
export function todoFor(data, keep = null) {
  const weeks = weekList(data)
  if (keep) {
    const w = weeks.find(x => x.module.id === keep.moduleId)
    if (!w) return null
    const items = counted(data, w.module.id).filter(h => keep.itemIds.includes(h.id))
    return items.length ? { moduleId: w.module.id, heading: `Still to tick from ${w.module.label}`, items } : null
  }
  for (const w of [...weeks].reverse()) {
    if (!weekWatched(data, w)) continue
    const items = counted(data, w.module.id).filter(h => !(data.ticks || []).includes(h.id))
    if (items.length) return { moduleId: w.module.id, heading: `Still to tick from ${w.module.label}`, items }
  }
  return null
}

// "Stuck on anything?": WhatsApp while their intake runs, the questions line otherwise
export function helpFor(data, product) {
  const whatsapp = httpsLink(data.whatsappUrl)
  if (whatsapp) {
    return {
      kind: 'whatsapp',
      text: product.settings?.stuck_text || 'WhatsApp Adam. Anything you want his eyes on before it goes out, send it over.',
      link: { href: whatsapp, label: 'Message Adam on WhatsApp' },
    }
  }
  // the product's questions line, in the words the masterclass board gives it (Adam, 3 Oct: C12)
  const email = product.questions_email
  return {
    kind: 'email',
    text: email ? `Email ${email} and Adam replies himself.` : 'Adam replies himself.',
    link: email ? { href: `mailto:${email}`, label: email } : null,
  }
}

// The whole home. heroShown: whether the published layout shows the next session; when Adam hides
// it, where they are leads the page instead.
export function feHome(data, product, nowMs, { heroShown = true } = {}) {
  const hero = heroFor(data, product, nowMs)
  const withHero = Boolean(hero) && heroShown
  const lead = leadFor(data, product, nowMs, { withHero })
  return {
    hero: withHero ? hero : null,
    heroShown: withHero,
    // beside a session, only a week to catch up on (or the certificate) earns its place
    lead: withHero && lead && !['carry', 'start', 'finished'].includes(lead.kind) ? null : lead,
    weeks: weekRows(data, product, nowMs),
    help: helpFor(data, product),
  }
}
